import { Box, Tabs, Typography, useTheme } from '@mui/material';
import useGetLoadBalancingGroup from 'hooks/useGetLoadBalancingGroup';
import { useLoadBalancingConfig } from 'hooks/useLoadBalancingConfig';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { time } from 'utils/common/convertNumbers';
import { deviceIsCharger } from 'utils/common/deviceTypeUtils';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import CustomTab from 'components/MuiCustom/Tab';

import { useAppSelector } from 'redux-store';

import { initialState } from '..';
import type { AddLoadBalancingGroupModalStageProps } from '..';
import DeviceGroup from './DeviceGroup';
import Devices from './Devices';

interface TabButton {
  index: number;
  title: string;
}

const TabButtons: Record<'devices' | 'groups', TabButton> = {
  devices: { index: 0, title: 'devices' },
  groups: { index: 1, title: 'groups' },
};

const AddDevicesToGroups: FC<AddLoadBalancingGroupModalStageProps> = ({
  state,
  setState,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const deviceGroups = useAppSelector(s => s.deviceGroups.items);
  const deviceList = useAppSelector(s => s.devices.items);

  const {
    deviceIds,
    groupIds,
    currentTab,
    selectedDevices,
    selectedGroup,
    loadBalancingData,
  } = state;

  const chargers = deviceList.filter(
    device =>
      !device.type.toLowerCase().includes('controller') &&
      !selectedDevices.includes(device.serial),
  );

  const loadBalancing = useGetLoadBalancingGroup(
    chargers.map(device => device.serial),
  );

  const filteredGroups = useMemo(
    () => deviceGroups.filter(group => groupIds.includes(group.id)),
    [deviceGroups, groupIds],
  );

  const uniqueDevices = useMemo(
    () => [
      ...new Set(
        filteredGroups.flatMap(group => group.devices ?? []).filter(Boolean),
      ),
    ],
    [filteredGroups],
  );

  const chargerIds = useMemo(
    () =>
      new Set(
        chargers
          .filter(device => deviceIsCharger(device.type))
          .map(charger => charger.id),
      ),
    [chargers],
  );

  const uniqueChargers = uniqueDevices.filter(id => chargerIds.has(id));

  const selectedSerials = useMemo(() => {
    if (currentTab === 'devices') {
      return chargers.filter(d => deviceIds.includes(d.id)).map(d => d.serial);
    }

    return chargers
      .filter(d => uniqueChargers.includes(d.id))
      .map(d => d.serial);
  }, [currentTab, chargers, deviceIds, uniqueChargers]);

  const conflictingSerials = useMemo(
    () =>
      selectedSerials.filter(serial =>
        loadBalancing.some(
          entry =>
            entry.data.serials.includes(serial) &&
            entry.data.loadBalancingGroupId,
        ),
      ),
    [loadBalancing, selectedSerials],
  );

  const setDevicesIds = useCallback(
    (ids: number[]) => {
      setState(prev => ({
        ...prev,
        deviceIds: ids,
      }));
    },
    [setState],
  );

  const setGroupIds = useCallback(
    (ids: number[]) => {
      setState(prev => ({ ...prev, groupIds: ids }));
    },
    [setState],
  );

  const setCurrentTabKey = useCallback(
    (tabIndex: number): void => {
      const tabEntry = Object.entries(TabButtons).find(
        ([, { index }]) => index === tabIndex,
      );

      if (tabEntry) {
        setState(prev => ({
          ...prev,
          currentTab: tabEntry[0] as 'devices' | 'groups',
          deviceIds: [],
          groupIds: [],
        }));
      }
    },
    [setState],
  );

  const { onSave, loading, error } = useLoadBalancingConfig({
    onSuccess: result => {
      const pendingDevices = result.results.filter(
        device => device.pending || !device.success,
      );

      if (selectedGroup && pendingDevices.length === 0) {
        setState(initialState);
        onClose();

        return;
      }

      setState(prev => ({
        ...prev,
        stage: 'success',
        selectedDevices: selectedSerials,
        pendingDevices,
      }));

      if (pendingDevices.length === 0) {
        setTimeout(() => {
          setState(initialState);
          onClose();
        }, time.seconds(5));
      }
    },
  });

  const handleAddDevicesToLoadBalancingGroup = useCallback(async () => {
    if (conflictingSerials.length > 0) {
      setState(prev => ({
        ...prev,
        stage: 'conflict',
        conflictingSerials,
        selectedDevices: selectedSerials,
      }));

      return;
    }

    if (!loadBalancingData) return;

    await onSave({
      serials: selectedSerials,
      log: loadBalancingData.loadBalancingGroupId,
      loe: loadBalancingData.loadBalancingEnabled,
      amp: loadBalancingData.maxTotalCurrent,
      sta: loadBalancingData.maxTotalCurrent,
      dyn: loadBalancingData.maxGridCurrent,
    });
  }, [
    conflictingSerials,
    setState,
    selectedSerials,
    loadBalancingData,
    onSave,
  ]);

  const isSubmitDisabled =
    loading ||
    (currentTab === 'devices' ? !deviceIds.length : !groupIds.length);

  return (
    <>
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: '70vh',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography variant="h2" mb={2} textAlign="center">
            {selectedGroup
              ? t('common:pages.device_groups.add_device_group.add_to_group')
              : t(
                  'common:pages.load_balancing.add_load_balancing_group.select_devices',
                )}
          </Typography>
          <Typography variant="body1" mb={1} textAlign="center">
            {t(
              'common:pages.load_balancing.add_load_balancing_group.description',
            )}
          </Typography>
        </Box>
        <Box mb={4}>
          <Tabs
            value={TabButtons[currentTab].index}
            onChange={(_, v: number) => setCurrentTabKey(v)}
            sx={{
              borderBottom: `1px solid ${theme.vars.palette.divider}`,
              width: '100%',
              justifyContent: 'space-around',
            }}
          >
            {Object.entries(TabButtons).map(([key, { index }]) => (
              <CustomTab
                key={key}
                tabIndex={index}
                label={t(
                  `common:pages.load_balancing.add_load_balancing_group.tabs.${key}`,
                )}
                iconPosition="start"
                sx={{ width: '50%', ml: 0, mr: 0 }}
                disableRipple
                data-testid={key}
              />
            ))}
          </Tabs>
        </Box>
        <Box
          sx={{
            height: '40vh',
            overflow: 'auto',
            width: '100%',
          }}
        >
          {currentTab === 'devices' ? (
            <Devices
              chargers={chargers}
              selectedDeviceIds={deviceIds}
              setSelectedDeviceIds={setDevicesIds}
            />
          ) : (
            <DeviceGroup
              deviceGroups={deviceGroups}
              selectedDeviceGroupIds={groupIds}
              setSelectedDeviceGroupIds={setGroupIds}
            />
          )}
        </Box>
      </Box>
      <ErrorText error={error} mt={1} mb={1} />
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          size="large"
          color="neutral"
          fullWidth
          disabled={loading}
          onClick={onClose}
          data-testid="go-back-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="flat"
          size="large"
          fullWidth
          disabled={isSubmitDisabled}
          onClick={handleAddDevicesToLoadBalancingGroup}
          data-testid="modal-save-button"
        >
          {t(
            selectedGroup
              ? 'common:pages.load_balancing.add_load_balancing_group.add_to_group'
              : 'common:pages.load_balancing.add_load_balancing_group.title',
          )}
        </Button>
      </Box>
    </>
  );
};

export default AddDevicesToGroups;
